import { useState, useEffect, useMemo, useCallback } from 'react';
import { LoaderCircle, Search, ChevronDown, ChevronRight, EyeOff } from 'lucide-react';
import { api } from '@/lib/api';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useProject } from '@/lib/ProjectContext';
import { useScanTask } from '@/lib/useScanTask';
import { useDashboardCache } from '@/lib/DashboardCacheContext';
import { usePermissions } from '@/lib/usePermissions';
import { useSubmissionFilter } from '@/lib/useSubmissionFilter';
import { useScanStatus } from '@/lib/ScanStatusContext';
import { invalidateConsistency, onConsistencyInvalidated } from '@/lib/consistencyInvalidation';

import { Telemetry } from '@/lib/telemetry';
import { ScanProgress } from '@/components/shared/ScanProgress';
import { ScanFooter } from '@/components/shared/ScanFooter';
import { OverrideHistory } from '@/components/shared/OverrideHistory';
import type { AbbreviationScanResponse, AbbreviationEntry, ScanIntegrityWarning } from '@/types/api';
import type { FindingTier } from '@/lib/findingClassification';
import { DataIntegrityNotice } from '@/components/shared/DataIntegrityNotice';
import { TierUncertaintyIndicator } from '@/components/shared/TierUncertaintyIndicator';
import { countByScope } from '@/lib/findingClassification';
import { ScopePill } from '@/components/shared/ScopePill';
import { ScopeFilterSelect, applyScopeFilter, type ScopeFilter } from '@/components/shared/ScopeFilterSelect';
import { formatSectionLabel } from '@/lib/formatSectionLabel';
import { ScopeBreakdownCard } from '@/components/shared/ScopeBreakdownCard';
import { SummaryStatCard } from '@/components/shared/SummaryStatCard';

type UIState = 'loading' | 'landing' | 'scanning' | 'results';
type StatusFilter = 'all' | 'undefined' | 'conflicting' | 'defined_elsewhere' | 'defined' | 'compound_code';

const PAGE_SIZE = 10;

export function AbbreviationsPage() {
  const { activeProject } = useProject();
  const { invalidate: invalidateDashboard } = useDashboardCache();
  const { canRunScans } = usePermissions();
  const { submissionId, submissions, isFiltering, classifyTier } = useSubmissionFilter();
  const [overlayApplicable, setOverlayApplicable] = useState(false);
  const [uiState, setUiState] = useState<UIState>('loading');
  const [data, setData] = useState<AbbreviationScanResponse | null>(null);
  const [integrityWarnings, setIntegrityWarnings] = useState<ScanIntegrityWarning[]>([]);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [hideDismissed, setHideDismissed] = useState(true);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>(isFiltering ? 'this-sub' : 'all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const scanStatus = useScanStatus();
  const scanBusy = scanStatus.isScanning('abbreviations');
  const projectId = activeProject?.id;
  const onScanComplete = useCallback(() => {
    invalidateDashboard();
    if (projectId) invalidateConsistency(projectId);
  }, [invalidateDashboard, projectId]);
  const { taskState, startTask, cancel: cancelScan, reset: resetTask, result: scanResult } = useScanTask<AbbreviationScanResponse>('abbreviations', onScanComplete);

  // Count dismissed items
  const dismissedCount = useMemo(
    () => data?.abbreviations.filter((a) => a.dismissed).length ?? 0,
    [data],
  );

  // A2F-462 follow-up: read via aggregate endpoint; extract the feature slice.
  const loadCached = useCallback(() => {
    if (!projectId) return;
    setUiState('loading');
    setError('');
    api.consistency.get(projectId, submissionId ?? undefined)
      .then((result) => {
        setOverlayApplicable(result.overlayApplicable);
        // A2F-832: capture warnings for the abbreviations scan.
        // Both abbreviations_inconsistent and abbreviations_undefined share
        // the "abbreviations" scan_key; the backend dedup guard tags warnings
        // with whichever feature_id appears first in FEATURE_IDS (currently
        // abbreviations_inconsistent). Filter for BOTH to stay resilient to
        // FEATURE_IDS reordering — one will always be empty.
        setIntegrityWarnings(
          (result.dataIntegrityWarnings ?? []).filter(
            (w) =>
              w.featureId === 'abbreviations_inconsistent' ||
              w.featureId === 'abbreviations_undefined',
          ),
        );
        const val = result.perFeature?.abbreviationsInconsistent?.value as AbbreviationScanResponse | null | undefined;
        if (val) {
          setData(val);
          setUiState('results');
        } else {
          // No cached results for this feature — check for a running task.
          api.tasks.findRunning(projectId, 'abbreviation_scan')
            .then((task) => {
              setUiState('scanning');
              startTask(task.taskId);
            })
            .catch(() => {
              setUiState('landing');
            });
        }
      })
      .catch(() => {
        setUiState('landing');
      });
  }, [projectId, submissionId, startTask]);

  useEffect(() => {
    loadCached();
  }, [loadCached]);

  // Refetch when any scan invalidates consistency for this project.
  useEffect(() => {
    return onConsistencyInvalidated((pid) => {
      if (pid === projectId) loadCached();
    });
  }, [projectId, loadCached]);

  // Handle scan result from background task
  useEffect(() => {
    if (!scanResult) return;
    scanStatus.endScan('abbreviations');
    if (scanResult.stoppedEarly && scanResult.errors?.length) {
      const msg = scanResult.errors[0].message || 'Scan failed';
      const isAuthError = /api[_\s]?key.*not set|authentication|unauthorized/i.test(msg);
      setError(
        isAuthError
          ? 'No LLM API key configured. Go to Settings \u2192 Manage App to add your API key.'
          : msg,
      );
      setUiState(data ? 'results' : 'landing');
      return;
    }
    setData(scanResult);
    setUiState('results');
    Telemetry.reviewResultsViewed({
      feature: 'abbreviations',
      finding_count: scanResult.abbreviations?.length ?? 0,
      stale_files: scanResult.staleFiles ?? 0,
    });
  }, [scanResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const startScan = async () => {
    if (!projectId) return;
    if (!scanStatus.startScan('abbreviations')) return; // already in progress
    setUiState('scanning');
    setError('');
    resetTask();
    try {
      const overlayId = overlayApplicable ? (submissionId ?? undefined) : undefined;
      const { taskId } = await api.abbreviations.scan(projectId, overlayId);
      startTask(taskId);
    } catch (err: unknown) {
      scanStatus.endScan('abbreviations');
      const msg = err instanceof Error ? err.message : 'Failed to start scan';
      setError(msg);
      setUiState(data ? 'results' : 'landing');
      Telemetry.errorSwallowed({ error_type: 'scan_start_failure', operation: 'abbreviations_scan', screen_name: '/review/abbreviations', error_message: msg });
    }
  };

  const rescan = () => {
    if (!projectId) return;
    Telemetry.reviewCacheCleared({ feature: 'abbreviations' });
    api.abbreviations.clearCache(projectId).then(() => startScan());
  };

  const toggleRow = (abbr: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(abbr)) next.delete(abbr);
      else next.add(abbr);
      return next;
    });
  };

  const handleDismiss = useCallback(async (abbreviation: string, reason: string) => {
    if (!projectId || !data) return;
    await api.abbreviations.dismiss(projectId, abbreviation, reason);
    Telemetry.findingDismissed({ feature: 'abbreviations', finding_id: abbreviation, has_rationale: reason.trim().length >= 10 });
    setData({
      ...data,
      abbreviations: data.abbreviations.map((a) =>
        a.abbreviation === abbreviation
          ? { ...a, dismissed: true, dismissReason: reason || '' }
          : a,
      ),
    });
  }, [projectId, data]);

  const handleUndismiss = useCallback(async (abbreviation: string) => {
    if (!projectId || !data) return;
    await api.abbreviations.undismiss(projectId, abbreviation);
    Telemetry.findingUndismissed({ feature: 'abbreviations', finding_id: abbreviation });
    setData({
      ...data,
      abbreviations: data.abbreviations.map((a) =>
        a.abbreviation === abbreviation
          ? { ...a, dismissed: false, dismissReason: null }
          : a,
      ),
    });
  }, [projectId, data]);

  // Filter + search
  const filtered = useMemo(() => {
    if (!data) return [];
    let items = data.abbreviations;
    if (hideDismissed) {
      items = items.filter((a) => !a.dismissed);
    }
    if (statusFilter !== 'all') {
      items = items.filter((a) => a.status === statusFilter);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      items = items.filter(
        (a) =>
          a.abbreviation.toLowerCase().includes(q) ||
          a.definitions.some((d) => d.definition.toLowerCase().includes(q)),
      );
    }
    return items;
  }, [data, statusFilter, searchText, hideDismissed]);

  // A2F-473: Classify findings into three tiers + sort
  type TieredEntry = AbbreviationEntry & { tier: FindingTier };
  const tieredItems = useMemo((): TieredEntry[] => {
    if (!isFiltering || !data) return filtered.map(f => ({ ...f, tier: 'new' as const }));

    const tierOrder: Record<FindingTier, number> = { new: 0, 'pre-existing': 1, inherited: 2 };
    const statusOrder: Record<string, number> = { conflicting: 0, undefined: 1, defined_elsewhere: 2, defined: 3 };

    const items: TieredEntry[] = filtered.map(item => ({
      ...item,
      tier: classifyTier(item, data.documentMap),
    }));

    // A2F-761: 'in-scope' is no longer offered in the dropdown but the sort
    // branch is kept in case it's reintroduced (or arrives via legacy state).
    // When showing 'in-scope': sort by tier → status.
    // When showing 'all' or 'this-sub': sort by status only (severity-first).
    if (scopeFilter === 'in-scope') {
      items.sort((a, b) =>
        tierOrder[a.tier] - tierOrder[b.tier]
        || (statusOrder[a.status] ?? 4) - (statusOrder[b.status] ?? 4)
      );
    } else {
      items.sort((a, b) => (statusOrder[a.status] ?? 4) - (statusOrder[b.status] ?? 4));
    }
    return items;
  }, [filtered, isFiltering, data, classifyTier, scopeFilter]);

  // Tier counts for context (combo box labels, etc.)
  const tierCounts = useMemo(() => {
    if (!isFiltering) return { new: 0, preExisting: 0, inherited: 0 };
    return {
      new: tieredItems.filter(i => i.tier === 'new').length,
      preExisting: tieredItems.filter(i => i.tier === 'pre-existing').length,
      inherited: tieredItems.filter(i => i.tier === 'inherited').length,
    };
  }, [tieredItems, isFiltering]);

  // Apply scope filter
  const displayItems = useMemo(() => {
    if (!isFiltering) return tieredItems;
    return applyScopeFilter(tieredItems, scopeFilter);
  }, [tieredItems, isFiltering, scopeFilter]);

  // A2F-487: per-card 3-bucket breakdown for the summary cards. Computed
  // from `tieredItems` (status/search/dismissed-filtered, but NOT scope-
  // filtered) so each card shows all three scope buckets regardless of
  // which one is active in the dropdown.
  const cardCounts = useMemo(() => {
    if (!isFiltering) return null;
    return {
      total: countByScope(tieredItems),
      undefined: countByScope(tieredItems.filter(a => a.status === 'undefined')),
      conflicting: countByScope(tieredItems.filter(a => a.status === 'conflicting')),
      definedElsewhere: countByScope(tieredItems.filter(a => a.status === 'defined_elsewhere')),
      defined: countByScope(tieredItems.filter(a => a.status === 'defined')),
    };
  }, [isFiltering, tieredItems]);

  // A2F-488: tile-click filter shortcut. Clicking any number on a summary
  // card sets both status + scope in one action. The active-cell highlight
  // in the cards is driven by the same state, giving two-way binding with
  // the Status and Scope dropdowns below.
  const handleTileFilter = useCallback((status: string, scope: ScopeFilter) => {
    setStatusFilter(status as StatusFilter);
    setScopeFilter(scope);
    Telemetry.reviewTileFilterClicked({ page: 'abbreviations', status, scope });
  }, []);

  // Non-filtering (no submission focus) variant: only status filtering applies.
  const handleStatusOnlyFilter = useCallback((status: string) => {
    setStatusFilter(status as StatusFilter);
    Telemetry.reviewTileFilterClicked({ page: 'abbreviations', status, scope: 'all' });
  }, []);

  const totalPages = Math.max(1, Math.ceil(displayItems.length / PAGE_SIZE));
  const pageItems = displayItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [statusFilter, searchText, hideDismissed, scopeFilter]);

  if (!activeProject) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-400">Select a project to scan abbreviations.</p>
        </div>
      </div>
    );
  }

  // --- Loading ---
  if (uiState === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <LoaderCircle className="w-6 h-6 text-brand-orange animate-spin" />
        <p className="text-sm text-slate-500">Checking for cached results...</p>
      </div>
    );
  }

  // --- Scanning ---
  if (uiState === 'scanning') {
    return <ScanProgress state={taskState} onCancel={cancelScan} featureLabel="abbreviations" />;
  }

  // --- Landing ---
  if (uiState === 'landing') {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center space-y-4">
          <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
            Chrona Review
          </p>
          <h1 className="text-xl font-bold text-brand-charcoal">Abbreviations</h1>
          <p className="text-sm text-slate-500">
            Scan your documents for undefined and conflicting abbreviations across the submission.
          </p>
          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          <button
            onClick={startScan}
            disabled={!canRunScans || scanBusy}
            title={!canRunScans ? 'Requires Contributor role' : scanBusy ? 'Scan in progress' : undefined}
            className={`inline-flex items-center gap-2 px-5 py-2.5 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors ${!canRunScans || scanBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {scanBusy ? 'Scan in progress\u2026' : 'Start Scan'}
          </button>
        </div>
      </div>
    );
  }

  // --- Results ---
  if (!data) return null;
  const { summary } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
          Chrona Review
        </p>
        <h1 className="text-xl font-bold text-brand-charcoal">Abbreviations</h1>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Summary cards — A2F-487/488: 3-bucket breakdown + click-to-filter */}
      {isFiltering && cardCounts ? (
        <div className="grid grid-cols-5 gap-4">
          <ScopeBreakdownCard
            label="Total"
            color="slate"
            {...cardCounts.total}
            statusValue="all"
            activeStatus={statusFilter}
            activeScope={scopeFilter}
            onFilter={handleTileFilter}
          />
          <ScopeBreakdownCard
            label="Undefined"
            color="red"
            {...cardCounts.undefined}
            statusValue="undefined"
            activeStatus={statusFilter}
            activeScope={scopeFilter}
            onFilter={handleTileFilter}
          />
          <ScopeBreakdownCard
            label="Conflicting"
            color="orange"
            {...cardCounts.conflicting}
            statusValue="conflicting"
            activeStatus={statusFilter}
            activeScope={scopeFilter}
            onFilter={handleTileFilter}
          />
          <ScopeBreakdownCard
            label="Defined Elsewhere"
            color="blue"
            {...cardCounts.definedElsewhere}
            statusValue="defined_elsewhere"
            activeStatus={statusFilter}
            activeScope={scopeFilter}
            onFilter={handleTileFilter}
          />
          <ScopeBreakdownCard
            label="Defined"
            color="green"
            {...cardCounts.defined}
            statusValue="defined"
            activeStatus={statusFilter}
            activeScope={scopeFilter}
            onFilter={handleTileFilter}
          />
        </div>
      ) : (
        <div className="grid grid-cols-5 gap-4">
          <SummaryStatCard label="Total" value={summary.total} color="slate"
            statusValue="all" activeStatus={statusFilter} onFilter={handleStatusOnlyFilter} />
          <SummaryStatCard label="Undefined" value={summary.undefined} color="red"
            statusValue="undefined" activeStatus={statusFilter} onFilter={handleStatusOnlyFilter} />
          <SummaryStatCard label="Conflicting" value={summary.conflicting} color="orange"
            statusValue="conflicting" activeStatus={statusFilter} onFilter={handleStatusOnlyFilter} />
          <SummaryStatCard label="Defined Elsewhere" value={summary.definedElsewhere} color="blue"
            statusValue="defined_elsewhere" activeStatus={statusFilter} onFilter={handleStatusOnlyFilter} />
          <SummaryStatCard label="Defined" value={summary.defined} color="green"
            statusValue="defined" activeStatus={statusFilter} onFilter={handleStatusOnlyFilter} />
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search abbreviations..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange bg-white"
        >
          <option value="all">All Statuses</option>
          <option value="undefined">Undefined</option>
          <option value="conflicting">Conflicting</option>
          <option value="defined_elsewhere">Defined Elsewhere</option>
          <option value="defined">Defined</option>
          <option value="compound_code">Compound Codes</option>
        </select>
        {dismissedCount > 0 && (
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideDismissed}
              onChange={(e) => setHideDismissed(e.target.checked)}
              className="rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30"
            />
            <EyeOff className="w-3.5 h-3.5 text-slate-400" />
            Hide dismissed ({dismissedCount})
          </label>
        )}
        {isFiltering && (
          <ScopeFilterSelect
            value={scopeFilter}
            onChange={setScopeFilter}
            thisSubCount={tierCounts.new}
            preExistingCount={tierCounts.preExisting}
            inheritedCount={tierCounts.inherited}
          />
        )}
        <div className="ml-auto">
          <button
            onClick={rescan}
            disabled={!canRunScans || scanBusy}
            title={!canRunScans ? 'Requires Contributor role' : scanBusy ? 'Scan in progress' : undefined}
            className={`px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors ${!canRunScans || scanBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {scanBusy ? 'Scan in progress\u2026' : 'Rescan'}
          </button>
        </div>
      </div>

      {/* A2F-832: scope-filter-independent integrity notice (BRIEF gotcha #9) */}
      {isFiltering && <DataIntegrityNotice warnings={integrityWarnings} />}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-4 py-3 font-medium text-slate-600 w-8" />
              <th className="text-left px-4 py-3 font-medium text-slate-600 w-8">Status</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Abbreviation</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Definition</th>
              {isFiltering && (
                <th className="text-left px-4 py-3 font-medium text-slate-600">Scope</th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 && (
              <tr>
                <td colSpan={isFiltering ? 5 : 4} className="px-4 py-8 text-center text-slate-400">
                  No abbreviations match your filters.
                </td>
              </tr>
            )}
            {pageItems.map((entry) => (
              <AbbreviationRow
                key={entry.abbreviation}
                entry={entry}
                tier={isFiltering ? (entry as TieredEntry).tier : null}
                tierConfident={entry.tierConfident}
                expanded={expandedRows.has(entry.abbreviation)}
                onToggle={() => toggleRow(entry.abbreviation)}
                documentMap={data.documentMap}
                onDismiss={handleDismiss}
                onUndismiss={handleUndismiss}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, displayItems.length)} of{' '}
            {displayItems.length}
          </span>
          <div className="flex gap-1">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <ScanFooter data={data} />
    </div>
  );
}

// --- Sub-components ---

/** Extract eCTD section number from filename, or truncate as fallback. */
function AbbreviationRow({
  entry,
  tier,
  tierConfident,
  expanded,
  onToggle,
  documentMap,
  onDismiss,
  onUndismiss,
}: {
  entry: AbbreviationEntry;
  tier: FindingTier | null;
  tierConfident?: boolean;
  expanded: boolean;
  onToggle: () => void;
  documentMap: Record<string, string>;
  onDismiss: (abbreviation: string, reason: string) => Promise<void>;
  onUndismiss: (abbreviation: string) => Promise<void>;
}) {
  const { canDismissFindings } = usePermissions();
  const [dismissing, setDismissing] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [busy, setBusy] = useState(false);

  const rowOpacity = entry.dismissed ? 'opacity-50' : '';

  const confirmDismiss = async () => {
    setBusy(true);
    try {
      await onDismiss(entry.abbreviation, dismissReason);
      setDismissing(false);
      setDismissReason('');
    } finally {
      setBusy(false);
    }
  };

  const confirmUndismiss = async () => {
    setBusy(true);
    try {
      await onUndismiss(entry.abbreviation);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr
        className={`border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer ${rowOpacity}`}
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={entry.status} feature="abbreviations" />
        </td>
        <td className="px-4 py-3 font-mono font-semibold text-brand-charcoal">
          {entry.abbreviation}
        </td>
        <td className="px-4 py-3 text-slate-600">
          {entry.definitions.length > 0 ? (
            <div className="space-y-0.5">
              {entry.definitions.map((def, i) => (
                <div key={i} className="text-sm">
                  <span>{def.definition}</span>
                  <span className="text-xs ml-1 text-slate-400">
                    ({def.documents.map(formatSectionLabel).join(', ')})
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </td>
        {tier && (
          <td className="px-4 py-3">
            <span className="inline-flex items-center gap-0.5">
              <ScopePill tier={tier} />
              {/* A2F-832: per-finding indicator when tier was conservatively assigned */}
              {tierConfident === false && <TierUncertaintyIndicator />}
            </span>
          </td>
        )}
      </tr>
      {expanded && (
        <tr className={`bg-slate-50/30 ${rowOpacity}`}>
          <td colSpan={tier ? 5 : 4} className="px-8 py-4 space-y-3">
            {/* Used in: section labels + file count */}
            {(entry.usedIn?.length ?? 0) > 0 && (
              <div className="text-xs text-slate-400">
                <span className="font-medium">Used in {entry.usedIn!.length} {entry.usedIn!.length === 1 ? 'document' : 'documents'}: </span>
                {entry.usedIn!.map((filename, i) => {
                  const docId = documentMap[filename];
                  return (
                    <span key={filename}>
                      {i > 0 && ', '}
                      {docId ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            api.documents.openLocal(docId).catch(() => {});
                          }}
                          className="text-brand-orange hover:underline"
                          title={`Open ${filename}`}
                        >
                          {formatSectionLabel(filename)}
                        </button>
                      ) : (
                        formatSectionLabel(filename)
                      )}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Dismiss / Restore actions */}
            {entry.dismissed ? (
              <div className="pt-2 border-t border-slate-100 space-y-2">
                {entry.dismissReason && (
                  <p className="text-xs text-slate-500">
                    <span className="font-medium">Dismiss reason:</span> {entry.dismissReason}
                  </p>
                )}
                <OverrideHistory entityType="abbreviation_dismissal" entityId="" />
                <button
                  onClick={(e) => { e.stopPropagation(); confirmUndismiss(); }}
                  disabled={busy || !canDismissFindings}
                  title={canDismissFindings ? undefined : 'Requires Contributor role'}
                  className={`px-3 py-1.5 text-xs font-medium text-brand-orange border border-brand-orange/30 rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50 ${!canDismissFindings ? 'cursor-not-allowed' : ''}`}
                >
                  {busy ? 'Restoring...' : 'Restore'}
                </button>
              </div>
            ) : dismissing ? (
              <div className="pt-2 border-t border-slate-100 space-y-2" onClick={(e) => e.stopPropagation()}>
                <div>
                  <textarea
                    placeholder="Reason for dismissal (required, minimum 10 characters)"
                    value={dismissReason}
                    onChange={(e) => setDismissReason(e.target.value)}
                    rows={2}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange resize-none"
                  />
                  <p className={`text-xs mt-1 ${dismissReason.trim().length >= 10 ? 'text-green-500' : 'text-slate-400'}`}>
                    {dismissReason.trim().length}/10 characters
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={confirmDismiss}
                    disabled={busy || dismissReason.trim().length < 10}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                  >
                    {busy ? 'Dismissing...' : 'Confirm'}
                  </button>
                  <button
                    onClick={() => { setDismissing(false); setDismissReason(''); }}
                    className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="pt-2 border-t border-slate-100">
                <button
                  onClick={(e) => { e.stopPropagation(); setDismissing(true); }}
                  disabled={!canDismissFindings}
                  title={canDismissFindings ? undefined : 'Requires Contributor role'}
                  className={`px-3 py-1.5 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors ${!canDismissFindings ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Dismiss
                </button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
